/* eslint-disable react/jsx-filename-extension */
import React, { useState } from 'react'
import { Box, Stack, Typography } from '@mui/material'
import Button from 'src/components/Button'
import LightBackground from 'src/components/LightBackground'
import { useAppDispatch, useAppSelect } from 'src/hooks/useRedux'
import PatternIcon from 'src/icons/PatternIcon'
import { validateNino } from 'src/utils/validateNino'

import { getCard } from '../../ducks/home.actions'
import TextContent from '../TextContent'

import FormHeader from './FormHeader'
import LastName from './LastName'
import NationalInsuranceNo from './NationalInsuranceNo'
import RegistrationNumber from './RegistrationNumber'
import Scheme from './SchemeInput'

const SmartCheckForm: React.FC = () => {
  const [showInfo, setInfoState] = useState<boolean>(() => false)
  const { payload, loading, error, scanBlocked } = useAppSelect(
    (state) => state.home,
  )
  const dispatch = useAppDispatch()
  const showNiInput = payload.ninoSupported === true

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (showNiInput) {
      if (!payload.cardSerialNumber && !payload.nino) {
        alert('Please enter either Registration number or National Insurance No')
        return
      }
      if (payload.nino && !validateNino(payload.nino)) {
        alert('Please enter a valid National Insurance Number, like AB123456C')
        return
      }
    } else {
      if (!payload.cardSerialNumber) {
        alert('Please enter Registration number')
        return
      }
    }

    // Dispatch unconditionally. If no captchaToken (or it's expired),
    // the axios wrapper (src/libs/axios/index.ts) detects the backend's
    // CAPTCHA_REQUIRED response, opens the captcha modal, waits for the
    // user to solve it, and then retries the /card request automatically
    // with the fresh token — the card check fires as soon as the captcha
    // is solved, no extra click needed.
    const { cardSerialNumber, nino, ...restPayload } = payload
    const apiPayload = {
      ...restPayload,
      ...(cardSerialNumber && { cardSerialNumber }),
      ...(nino && { nino }),
    }

    dispatch(getCard(apiPayload))
  }

  const submitDisabled = loading || scanBlocked

  return (
    <LightBackground>
      <FormHeader
        setInfoState={() => {
          setInfoState(true)
        }}
      />
      <Box
        component="div"
        sx={{
          position: 'relative',
          width: '100%',
          pointerEvents: 'none',
        }}
      >
        <PatternIcon
          sx={{
            position: 'absolute',
            width: 340,
            height: 720,
            left: 0,
            top: 0,
            translate: '0 -49.7%',
            pointerEvents: 'none',
            '@media (max-width: 590px)': {
              width: 260,
              height: 550,
              translate: '0 -48%',
            },
          }}
        />
      </Box>
      <Box
        component="form"
        onSubmit={onSubmit}
        sx={{
          width: '70%',
          maxWidth: '50%',
          zIndex: 9,
          '@media (max-width: 590px)': {
            width: '100%',
            maxWidth: '100%',
            px: 4,
          },
        }}
      >
        <Stack
          justifyContent="center"
          alignItems="center"
          sx={{
            mt: 10,
            gap: 10,
            '@media (max-width: 590px)': {
              mt: 5,
              gap: 5,
            },
          }}
        >
          {showInfo ? (
            <Stack direction="row" spacing={10} sx={{ width: '100%' }}>
              <TextContent
                onClose={() => {
                  setInfoState(false)
                }}
              />
            </Stack>
          ) : showNiInput ? (
            // CSCS/LISS layout
            <>
              <Stack direction="row" spacing={20} sx={{
                width: '100%',
                '@media (max-width: 1000px)': {
                  flexDirection: 'column',
                },
                '@media (max-width: 590px)': {
                  flexDirection: 'column',
                  gap: 5,
                },
              }}>
                <Scheme />
                <Box sx={{
                  width: '100%',
                  '@media (max-width: 1000px)': {
                    marginLeft: '22px !important',
                  },
                  '@media (max-width: 590px)': {
                    marginLeft: '0 !important',
                  },
                }} ><LastName /></Box>
              </Stack>
              <Box sx={{
                border: '2px solid',
                borderColor: 'grey.400',
                borderRadius: 1,
                p: 10,
                width: '100%',
                backgroundColor: 'rgba(245, 245, 245, 0.7)',
                '@media (max-width: 1000px)': {
                  marginLeft: '47px !important',
                },
                '@media (max-width: 590px)': {
                  marginLeft: '0 !important',
                  p: 4,
                },
              }}>
                <Typography sx={{ mb: 2 }}>Enter at least one identifier <span style={{ color: '#F90000' }}>*</span></Typography>
                <Stack direction="row" alignItems="center" spacing={4} sx={{
                  '@media (max-width: 1000px)': {
                    flexDirection: 'column',
                  },
                  '@media (max-width: 590px)': {
                    flexDirection: 'column',
                    gap: 2,
                  },
                }}>
                  <Box sx={{
                    flex: 1,
                    width: '100%',
                    pl: 10,
                    '@media (max-width: 1000px)': {
                      marginRight: '20px !important',
                    },
                    '@media (max-width: 590px)': {
                      pl: 0,
                      marginRight: '0 !important',
                    },
                  }}><RegistrationNumber /></Box>
                  <Typography sx={{
                    pt: 7,
                    px: 2,
                    '@media (max-width: 590px)': {
                      pt: 2,
                    },
                  }}>OR</Typography>
                  <Box sx={{
                    flex: 1,
                    width: '100%',
                    pr: 10,
                    '@media (max-width: 1000px)': {
                      marginRight: '20px !important',
                      marginLeft: '47px !important',
                    },
                    '@media (max-width: 590px)': {
                      pr: 0,
                      marginRight: '0 !important',
                      marginLeft: '0 !important',
                    },
                  }}><NationalInsuranceNo /></Box>
                </Stack>
              </Box>
            </>
          ) : (
            // Default layout for other schemes
            <Stack direction="row" spacing={10} sx={{
              width: '100%',
              '@media (max-width: 1000px)': {
                flexDirection: 'column',
              },
              '@media (max-width: 590px)': {
                flexDirection: 'column',
                gap: 5,
              },
            }}>
              <Scheme />
              <RegistrationNumber />
              <LastName />
            </Stack>
          )}
          <Stack
            direction="row"
            justifyContent="flex-end"
            alignItems="center"
            sx={{
              width: '100%',
              '@media (max-width: 1000px)': {
                width: '100%',
                marginLeft: '47px !important',
                flexDirection: 'column',
              },
              '@media (max-width: 590px)': {
                marginLeft: '0 !important',
                flexDirection: 'column',
                alignItems: 'stretch',
              },
            }}
          >
            <Button
              title={loading ? 'Loading' : 'Check Card'}
              type="submit"
              disabled={submitDisabled}
              sx={{
                '@media (max-width: 1000px)': {
                  marginRight: 'auto',
                  marginTop: '20px'
                },
                '@media (max-width: 590px)': {
                  marginTop: '16px',
                },
              }}
            />
          </Stack>

          {error && <Typography color="error">{error}</Typography>}
        </Stack>
      </Box>
    </LightBackground >
  )
}

export default SmartCheckForm
